import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Users, Plus, X, Save, Search, Phone, Mail, Building, MapPin,
  Trash2, Edit2, ArrowLeft, User, Briefcase, Tag, Globe
} from 'lucide-react';
import CompanyLogo from '../components/CompanyLogo';
import LanguageToggle from '../components/LanguageToggle';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Contact roles with colors
const CONTACT_ROLES = {
  homeowner: { label: 'Homeowner', labelEs: 'Propietario', color: '#3b82f6' },
  architect: { label: 'Architect', labelEs: 'Arquitecto', color: '#8b5cf6' },
  engineer: { label: 'Engineer', labelEs: 'Ingeniero', color: '#6366f1' },
  inspector: { label: 'Inspector', labelEs: 'Inspector', color: '#f59e0b' },
  subcontractor: { label: 'Subcontractor', labelEs: 'Subcontratista', color: '#10b981' },
  vendor: { label: 'Vendor', labelEs: 'Proveedor', color: '#06b6d4' },
  supplier: { label: 'Supplier', labelEs: 'Suplidor', color: '#14b8a6' },
  permit_office: { label: 'Permit Office', labelEs: 'Oficina de Permisos', color: '#f97316' },
  hoa: { label: 'HOA', labelEs: 'HOA', color: '#ec4899' },
  electrician: { label: 'Electrician', labelEs: 'Electricista', color: '#eab308' },
  plumber: { label: 'Plumber', labelEs: 'Plomero', color: '#0ea5e9' },
  roofer: { label: 'Roofer', labelEs: 'Techador', color: '#78716c' },
  other: { label: 'Other', labelEs: 'Otro', color: '#6b7280' }
};

export default function Contacts() {
  const { language } = useLanguage();
  
  // Data state
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    role: 'other',
    company: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    notes: '',
    tags: [],
    website: '',
    google_business_profile: ''
  });
  const [newTag, setNewTag] = useState('');

  // Fetch contacts
  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/universal/all`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  // CRUD operations
  const saveContact = async () => {
    if (!form.name.trim()) {
      toast.error(language === 'es' ? 'Nombre requerido' : 'Name is required');
      return;
    }
    
    try {
      const url = editingContact 
        ? `${BACKEND_URL}/api/contacts/${editingContact.id}`
        : `${BACKEND_URL}/api/contacts`;
      
      const method = editingContact ? 'PUT' : 'POST';
      
      const contactData = {
        ...form,
        contact_type: 'universal'
      };
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData)
      });
      
      if (response.ok) {
        toast.success(editingContact 
          ? (language === 'es' ? 'Contacto actualizado' : 'Contact updated')
          : (language === 'es' ? 'Contacto creado' : 'Contact created')
        );
        fetchContacts();
        closeModal();
      } else {
        toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
      }
    } catch (error) {
      console.error('Error saving contact:', error);
      toast.error(language === 'es' ? 'Error al guardar' : 'Failed to save');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este contacto?' : 'Delete this contact?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/${contactId}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success(language === 'es' ? 'Contacto eliminado' : 'Contact deleted');
        fetchContacts();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  // Modal handlers
  const openNewModal = () => {
    setEditingContact(null);
    setForm({
      name: '',
      role: 'other',
      company: '',
      email: '',
      phone: '',
      mobile: '',
      address: '',
      notes: '',
      tags: [],
      website: '',
      google_business_profile: ''
    });
    setShowModal(true);
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact.name || '',
      role: contact.role || 'other',
      company: contact.company || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      address: contact.address || '',
      notes: contact.notes || '',
      tags: contact.tags || [],
      website: contact.website || '',
      google_business_profile: contact.google_business_profile || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingContact(null);
  };

  // Tag handlers
  const addTag = () => {
    if (newTag.trim() && !form.tags.includes(newTag.trim())) {
      setForm({ ...form, tags: [...form.tags, newTag.trim()] });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tagToRemove) });
  };

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = !searchQuery || 
      contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.phone?.includes(searchQuery);
    
    const matchesRole = roleFilter === 'all' || contact.role === roleFilter;
    
    return matchesSearch && matchesRole;
  });

  // Group contacts by role
  const groupedContacts = filteredContacts.reduce((acc, contact) => {
    const role = contact.role || 'other';
    if (!acc[role]) acc[role] = [];
    acc[role].push(contact);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-amber-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <nav className="container mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/admin" className="flex items-center text-gray-600 hover:text-gray-900">
                <ArrowLeft className="h-5 w-5 mr-2" />
                <span className="hidden sm:inline">{language === 'es' ? 'Volver' : 'Back'}</span>
              </Link>
              <div className="h-6 w-px bg-gray-300" />
              <Link to="/" className="hover:opacity-80">
                <CompanyLogo className="h-8 w-auto" />
              </Link>
            </div>
            
            <div className="flex items-center space-x-2">
              <h1 className="text-lg font-bold text-gray-800 flex items-center">
                <Users className="mr-2 h-5 w-5" style={{ color: COMPANY.primaryColor }} />
                {language === 'es' ? 'Contactos' : 'Contacts'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <LanguageToggle />
              <Button 
                onClick={openNewModal}
                style={{ backgroundColor: COMPANY.primaryColor }}
                className="text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Nuevo Contacto' : 'New Contact'}
              </Button>
            </div>
          </div>
        </nav>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6">
        {/* Search and Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={language === 'es' ? 'Buscar contactos...' : 'Search contacts...'}
                  className="pl-10"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder={language === 'es' ? 'Filtrar por rol' : 'Filter by role'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'es' ? 'Todos los roles' : 'All roles'}</SelectItem>
                  {Object.entries(CONTACT_ROLES).map(([key, role]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                        {language === 'es' ? role.labelEs : role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold" style={{ color: COMPANY.primaryColor }}>{contacts.length}</div>
              <div className="text-sm text-gray-500">{language === 'es' ? 'Total Contactos' : 'Total Contacts'}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-green-600">
                {contacts.filter(c => c.role === 'subcontractor').length}
              </div>
              <div className="text-sm text-gray-500">{language === 'es' ? 'Subcontratistas' : 'Subcontractors'}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-cyan-600">
                {contacts.filter(c => c.role === 'vendor' || c.role === 'supplier').length}
              </div>
              <div className="text-sm text-gray-500">{language === 'es' ? 'Proveedores' : 'Vendors'}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-3xl font-bold text-amber-600">
                {contacts.filter(c => c.role === 'inspector').length}
              </div>
              <div className="text-sm text-gray-500">{language === 'es' ? 'Inspectores' : 'Inspectors'}</div>
            </CardContent>
          </Card>
        </div>

        {/* Contacts Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">{language === 'es' ? 'Cargando...' : 'Loading...'}</p>
          </div>
        ) : filteredContacts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">
                {searchQuery || roleFilter !== 'all'
                  ? (language === 'es' ? 'No se encontraron contactos' : 'No contacts found')
                  : (language === 'es' ? 'No hay contactos' : 'No contacts yet')
                }
              </h3>
              <p className="text-gray-500 mb-4">
                {language === 'es' 
                  ? 'Agregue sus contactos de subcontratistas, proveedores, inspectores y más.'
                  : 'Add your subcontractors, vendors, inspectors, and more.'}
              </p>
              <Button onClick={openNewModal} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                <Plus className="h-4 w-4 mr-2" />
                {language === 'es' ? 'Agregar Primer Contacto' : 'Add First Contact'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredContacts.map(contact => {
              const roleConfig = CONTACT_ROLES[contact.role] || CONTACT_ROLES.other;
              return (
                <Card key={contact.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                          style={{ backgroundColor: roleConfig.color }}
                        >
                          {contact.name?.charAt(0).toUpperCase() || '?'}
                        </div>
                        <div>
                          <h3 className="font-semibold">{contact.name}</h3>
                          {contact.company && (
                            <p className="text-sm text-gray-500 flex items-center">
                              <Building className="h-3 w-3 mr-1" />
                              {contact.company}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge 
                        variant="outline" 
                        style={{ borderColor: roleConfig.color, color: roleConfig.color }}
                      >
                        {language === 'es' ? roleConfig.labelEs : roleConfig.label}
                      </Badge>
                    </div>
                    
                    <div className="space-y-1 text-sm mb-3">
                      {contact.phone && (
                        <p className="flex items-center text-gray-600">
                          <Phone className="h-3 w-3 mr-2 text-gray-400" />
                          <a href={`tel:${contact.phone}`} className="hover:text-amber-600">{contact.phone}</a>
                        </p>
                      )}
                      {contact.mobile && (
                        <p className="flex items-center text-gray-600">
                          <Phone className="h-3 w-3 mr-2 text-gray-400" />
                          <a href={`tel:${contact.mobile}`} className="hover:text-amber-600">{contact.mobile}</a>
                          <span className="text-xs text-gray-400 ml-1">(mobile)</span>
                        </p>
                      )}
                      {contact.email && (
                        <p className="flex items-center text-gray-600">
                          <Mail className="h-3 w-3 mr-2 text-gray-400" />
                          <a href={`mailto:${contact.email}`} className="hover:text-amber-600 truncate">{contact.email}</a>
                        </p>
                      )}
                      {contact.address && (
                        <p className="flex items-center text-gray-600">
                          <MapPin className="h-3 w-3 mr-2 text-gray-400" />
                          <span className="truncate">{contact.address}</span>
                        </p>
                      )}
                      {contact.website && (
                        <p className="flex items-center text-gray-600">
                          <Globe className="h-3 w-3 mr-2 text-gray-400" />
                          <a href={contact.website} target="_blank" rel="noopener noreferrer" className="hover:text-amber-600 truncate">{contact.website}</a>
                        </p>
                      )}
                      {contact.google_business_profile && (
                        <p className="flex items-center text-gray-600">
                          <Globe className="h-3 w-3 mr-2 text-green-500" />
                          <a href={contact.google_business_profile} target="_blank" rel="noopener noreferrer" className="hover:text-amber-600 truncate">Google Business</a>
                        </p>
                      )}
                    </div>
                    
                    {contact.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        {contact.tags.map((tag, i) => (
                          <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <div className="flex gap-2 pt-2 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => openEditModal(contact)}
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'Editar' : 'Edit'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => deleteContact(contact.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Contact Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingContact 
                    ? (language === 'es' ? 'Editar Contacto' : 'Edit Contact')
                    : (language === 'es' ? 'Nuevo Contacto' : 'New Contact')
                  }
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={closeModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Name */}
              <div>
                <Label>{language === 'es' ? 'Nombre' : 'Name'} *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={language === 'es' ? 'Nombre completo' : 'Full name'}
                  className="mt-1"
                />
              </div>
              
              {/* Role */}
              <div>
                <Label>{language === 'es' ? 'Rol / Categoría' : 'Role / Category'}</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CONTACT_ROLES).map(([key, role]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: role.color }} />
                          {language === 'es' ? role.labelEs : role.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Company */}
              <div>
                <Label>{language === 'es' ? 'Compañía' : 'Company'}</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder={language === 'es' ? 'Nombre de la empresa' : 'Company name'}
                  className="mt-1"
                />
              </div>
              
              {/* Phone / Mobile */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Móvil' : 'Mobile'}</Label>
                  <Input
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    placeholder="(555) 987-6543"
                    className="mt-1"
                  />
                </div>
              </div>
              
              {/* Email */}
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@example.com"
                  className="mt-1"
                />
              </div>
              
              {/* Address */}
              <div>
                <Label>{language === 'es' ? 'Dirección' : 'Address'}</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder={language === 'es' ? 'Dirección completa' : 'Full address'}
                  className="mt-1"
                />
              </div>
              
              {/* Website */}
              <div>
                <Label>Website</Label>
                <Input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://www.example.com"
                  className="mt-1"
                />
              </div>
              
              {/* Google Business Profile */}
              <div>
                <Label>Google Business Profile</Label>
                <Input
                  type="url"
                  value={form.google_business_profile}
                  onChange={(e) => setForm({ ...form, google_business_profile: e.target.value })}
                  placeholder="https://g.page/business-name"
                  className="mt-1"
                />
              </div>
              
              {/* Tags */}
              <div>
                <Label>{language === 'es' ? 'Etiquetas' : 'Tags'}</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder={language === 'es' ? 'Agregar etiqueta' : 'Add tag'}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  />
                  <Button type="button" variant="outline" onClick={addTag}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {form.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {form.tags.map((tag, i) => (
                      <span 
                        key={i} 
                        className="inline-flex items-center bg-amber-100 text-amber-800 text-xs px-2 py-1 rounded"
                      >
                        {tag}
                        <button 
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="ml-1 text-amber-600 hover:text-amber-800"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Notes */}
              <div>
                <Label>{language === 'es' ? 'Notas' : 'Notes'}</Label>
                <Textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder={language === 'es' ? 'Notas adicionales...' : 'Additional notes...'}
                  className="mt-1"
                  rows={3}
                />
              </div>
              
              {/* Actions */}
              <div className="flex justify-between pt-4 border-t">
                {editingContact && (
                  <Button 
                    variant="destructive" 
                    onClick={() => { deleteContact(editingContact.id); closeModal(); }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Eliminar' : 'Delete'}
                  </Button>
                )}
                <div className={`flex gap-2 ${editingContact ? '' : 'ml-auto'}`}>
                  <Button variant="outline" onClick={closeModal}>
                    {language === 'es' ? 'Cancelar' : 'Cancel'}
                  </Button>
                  <Button 
                    onClick={saveContact}
                    disabled={!form.name.trim()}
                    style={{ backgroundColor: COMPANY.primaryColor }}
                    className="text-white"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'Guardar' : 'Save'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
